import { useFacilityScreenerSettings } from '@/hooks/screener-settings';
import { ScreenerSettingName } from '@/hooks/screener-settings/screenerSettingName';
import {
  type FacilityPayload,
  isActiveFacility,
  useFacilitiesQuery
} from '@/hooks/useFacilitiesQuery';

import { useAccessibleFacilityIds } from '../../hooks/useInstanceAccessibility';

interface InboundFacilities {
  facilities: FacilityPayload[];
  isLoading: boolean;
  isError: boolean;
  retry: () => void;
}

export function useInboundFacilities(): InboundFacilities {
  const facilitiesQuery = useFacilitiesQuery().useFindAll();
  const accessibleIds = useAccessibleFacilityIds();
  const candidates = (facilitiesQuery.data ?? []).filter(
    (facility) => isActiveFacility(facility) && accessibleIds.has(facility.id)
  );
  const settingsQuery = useFacilityScreenerSettings(
    candidates.map((facility) => facility.id),
    {
      staleTime: 5 * 60 * 1000
    }
  );
  const enabledIds = new Set(
    settingsQuery.data
      ?.filter(
        (entry) =>
          entry.settings?.[ScreenerSettingName.InboundDocuments] === 'enabled'
      )
      .map((entry) => entry.facility_id)
  );
  const isError =
    facilitiesQuery.isError || (candidates.length > 0 && settingsQuery.isError);
  return {
    facilities: isError
      ? []
      : candidates.filter((facility) => enabledIds.has(facility.id)),
    isLoading:
      facilitiesQuery.data === undefined ||
      (candidates.length > 0 && settingsQuery.data === undefined),
    isError,
    retry: () => {
      void facilitiesQuery.refetch();
      void settingsQuery.refetch();
    }
  };
}
